import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cloneDeep } from 'es-toolkit';

import { Box, Paragraph, Toast, useToast } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import { PhotoDetailsModal } from '@zg-rentals/maintenance-utils';

import { useSelector } from '../../selectors/useSelector';

import { useDeletePhoto, useSortPhoto, useUpdatePhotoCaption } from '../../hooks/queries/photos';

import PropertiesActions from '../../actions/PropertiesActions';

import { getPhotoUrls } from '../../utils/propertiesPageUtils';

import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';

import PhotoUploadDropzone from '../../components/PhotoUpload';

import SortableItem from './components/SortableItem';
import DiscriminatoryContentErrorAlert from './components/DiscriminatoryContentErrorAlert';
import { StyledOrderedList } from './styles';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';
import type { DragEndEvent } from '@dnd-kit/core';

type Props = { uploadingCount: number; setUploadingCount: Dispatch<SetStateAction<number>> };

const PhotoGallery = ({ uploadingCount, setUploadingCount }: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const [photos, setPhotos] = useState<Array<PhotoProps>>([]);
  const [clickedPhotoIdx, setClickedPhotoIdx] = useState<number | undefined | null>();
  const [discriminatoryContentErrors, setDiscriminatoryContentErrors] = useState<
    Array<{ photoId: string; caption: string }>
  >([]);

  const {
    app: { isRunningInProd },
    user: {
      features: { canUpdateListing },
    },
    properties: {
      currentProperty,
      currentProperty: {
        isMultifamily,
        isFeed,
        isArchived,
        listingAlias,
        restrictions: { isUpdateListingRestricted },
        selfServeOnboardingStatus: { isSelfServeOnboardingInProgress, isUpdateRestricted: isSSOBUpdateRestricted },
      },
    },
  } = useSelector((state) => state);

  const { mutate: sortPhotos } = useSortPhoto(listingAlias);
  const { mutateAsync: deletePhoto } = useDeletePhoto(discriminatoryContentErrors, setDiscriminatoryContentErrors);
  const { mutateAsync: updatePhotoCaption } = useUpdatePhotoCaption();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // there isn't a way to disable so basically the hack is to not engage the drag and drop till the photo is moved greater than 3000px which is not common on desktop
        distance: isFeed ? 3000 : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: isFeed ? 3000 : 5,
      },
    }),
  );

  useEffect(() => {
    if (currentProperty?.photos) {
      setPhotos(currentProperty.photos);
    }
  }, [currentProperty?.photos]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (uploadingCount) {
      return;
    }

    const { active, over } = event;

    if (active.id !== over?.id) {
      setPhotos((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        const sortedArray: Array<PhotoProps> = arrayMove(items, oldIndex, newIndex);
        const sortedArraysWithUpdatedRank = sortedArray.map((photo, index) => ({ ...photo, rank: index + 1 }));

        sortPhotos(sortedArraysWithUpdatedRank, {
          onError: () => enqueueToast(<Toast tone="critical">Error occurred</Toast>),
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
            queryClient.invalidateQueries({ queryKey: ['getMultiplexUnits'] });
            queryClient.invalidateQueries({ queryKey: ['getGroupableProperties'] });
          },
        });

        dispatch(PropertiesActions.updateCurrentProperty({ photos: sortedArraysWithUpdatedRank }));
        return sortedArraysWithUpdatedRank;
      });
    }
  };

  const handleDeletePhoto = async ({
    photoIdAtSlidePosition,
    onComplete,
  }: {
    photoIdAtSlidePosition: string;
    onComplete: () => void;
  }) => {
    try {
      await deletePhoto(photoIdAtSlidePosition);

      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      queryClient.invalidateQueries({ queryKey: ['getMultiplexUnits'] });
      queryClient.invalidateQueries({ queryKey: ['getGroupableProperties'] });

      if (!isMultifamily) {
        enqueueToast(<Toast tone="success">Photo deleted</Toast>);
      }
    } catch {
      enqueueToast(<Toast tone="critical">There was an error with deleting the photo</Toast>);
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleCaptionUpdate = async ({
    photoId,
    caption,
    onComplete,
  }: {
    photoId: string;
    caption: string;
    onComplete: () => void;
  }) => {
    try {
      await updatePhotoCaption({ photoId, caption });
    } catch (err) {
      if (err?.error?.message) {
        const copyPhotos = cloneDeep(photos);
        const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

        copyPhotos[indexToUpdate].error = err?.error?.message || 'There was an error saving your caption.';
        copyPhotos[indexToUpdate].caption = caption;
        dispatch(PropertiesActions.updateCurrentProperty({ photos: copyPhotos }));

        if (err?.error?.message.includes('Good Neighbor Policy')) {
          const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
          copyDiscriminatoryContent.push({ photoId, caption });
          setDiscriminatoryContentErrors(copyDiscriminatoryContent);
        }
      }
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleClearError = ({ photoAtSlidePosition }: { photoAtSlidePosition: PhotoProps }) => {
    if (photoAtSlidePosition.error) {
      const copyPhotos = cloneDeep(photos);
      const indexToUpdate = copyPhotos.findIndex((copyPhoto) => copyPhoto.id === photoAtSlidePosition.id);

      copyPhotos[indexToUpdate].error = undefined;

      dispatch(PropertiesActions.updateCurrentProperty({ photos: copyPhotos }));

      const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
      const errorIndexToUpdate = copyDiscriminatoryContent.findIndex(
        (error) => error.photoId === photoAtSlidePosition.id,
      );
      copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

      setDiscriminatoryContentErrors(copyDiscriminatoryContent);
    }
  };

  if (!photos) {
    return null;
  }

  return (
    <div data-testid="PhotoGallery">
      {!isFeed && <Paragraph css={{ marginBottom: '400' }}>Drag and drop to reorder</Paragraph>}

      {discriminatoryContentErrors.length > 0 && (
        <DiscriminatoryContentErrorAlert errors={discriminatoryContentErrors} />
      )}

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          columnGap: '400',
          rowGap: '400',
        }}
      >
        {!isFeed && (
          <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 1' }}>
            <PhotoUploadDropzone
              isSmallDropzone={true}
              setUploadingCount={setUploadingCount}
              uploadingCount={uploadingCount}
            />
          </Box>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos} strategy={rectSortingStrategy}>
            <StyledOrderedList aria-label="property photos">
              {(photos as Array<PhotoProps>).map((photo: PhotoProps, idx: number) => (
                <SortableItem
                  key={photo.id}
                  errors={discriminatoryContentErrors}
                  handlePhotoClick={() => setClickedPhotoIdx(idx)}
                  photo={photo}
                  index={idx}
                  isDisabled={!!uploadingCount}
                  isRunningInProd={isRunningInProd}
                />
              ))}
            </StyledOrderedList>

            {uploadingCount > 0 &&
              Array.from(Array(uploadingCount)).map((_, idx) => (
                <SortableItem
                  key={`placeholder-photo-${idx}`}
                  isUploading={uploadingCount > 0}
                  photo={{ id: '' }}
                  index={idx}
                  isDisabled={true}
                  isRunningInProd={isRunningInProd}
                />
              ))}
          </SortableContext>
        </DndContext>
      </Box>

      {!!(clickedPhotoIdx || clickedPhotoIdx === 0) && (
        <PhotoDetailsModal
          additionalDisabledChecks={
            Boolean(uploadingCount > 0) ||
            isFeed ||
            !canUpdateListing ||
            isArchived ||
            isUpdateListingRestricted ||
            (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted)
          }
          idx={clickedPhotoIdx}
          onCaptionUpdate={handleCaptionUpdate}
          onClearError={handleClearError}
          onClose={() => setClickedPhotoIdx(null)}
          onDeletePhoto={handleDeletePhoto}
          photos={photos}
          getPhotoUrls={getPhotoUrls}
          isRunningInProd={isRunningInProd}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
